import React from "react";
import { useTranslation } from "react-i18next";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import { useAuth0 } from "@auth0/auth0-react";
import { OrgPhoneNumber, PHONE_TYPE_LABELS, readOrgPhoneNumbers } from "services/phone-numbers.service";

type Props = { orgId: number };

const PhoneNumbersList = ({ orgId }: Props) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const [rows, setRows] = React.useState<Array<{ id: string; number: string; type: string }>>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (orgId <= 0) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data, error: apiError } = await readOrgPhoneNumbers(token, orgId);
        if (cancelled) return;
        if (apiError) {
          setError(apiError.message);
          return;
        }
        setRows(
          (data ?? []).map((p: OrgPhoneNumber) => ({
            id: String(p.phoneNumberId),
            number: p.phoneNumber,
            type: PHONE_TYPE_LABELS[p.phoneNumberType] ?? String(p.phoneNumberType),
          }))
        );
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, getAccessTokenSilently]);

  return (
    <div>
      <Table<{ id: string; number: string; type: string }>
        loading={loading}
        columns={{ number: t("clinics.phoneNumbers.columnNumber"), type: t("clinics.phoneNumbers.columnType") }}
        rows={rows}
      />
      {error && <div className="mt-3 text-sm text-red-500">{error}</div>}

      <div className="mt-4">
        <PaginationBar from={1} to={rows.length} total={rows.length} />
      </div>
    </div>
  );
};

export default PhoneNumbersList;
